import React from 'react';
import { productTour } from './builders';

const BACK = { backTo: '/investments/debt', backLabel: 'Back to Debt' };

// Pages that record each purchase as Price × Units (Govt Bonds, Corporate Bonds,
// NCDs, Other Debt). They have no live prices or interest, only what you paid.
//   label:  name of the first field in the Add form (e.g. "Bond Series")
//   plural: what one row is, in the plural (e.g. "bond series")
const priceUnitsTour = ({ name, intro, label, plural, tip }) => productTour({
  name,
  intro,
  add: (
    <>
      <p>Enter the <b>{label}</b>, the <b>Date of Purchase</b>, the <b>Price</b> you paid per unit and the number of <b>Units</b>. <b>Amount Invested</b> is worked out for you (Price × Units).</p>
      <p>Bought more of the same one later? Pick it from the list so the purchases are grouped together.{tip ? ` ${tip}` : ''}</p>
    </>
  ),
  summary: (
    <p><b>Total</b> is the amount you've invested across all your {name}, at the price you paid. Interest and market prices aren't tracked here, so it only changes when you add, edit or delete a purchase.</p>
  ),
  holdings: (
    <>
      <p>One row for each of your {plural}, combining all your purchases of it. <b>Value</b> is what you paid and <b>Allocation</b> is its share of your total.</p>
      <p><b>Click a row</b> to see every purchase, then use the <b>⋮</b> button to edit or delete one. Tap a column heading to sort.</p>
    </>
  ),
  ...BACK,
});

// Provident funds (PPF, EPF): deposits only, no interest worked out.
const providentInterestStep = (fund) => ({
  title: 'Recording interest',
  body: (
    <p>Mickles doesn't add {fund} interest for you. To match your passbook, add the interest credited each year as a deposit too, with a note such as <b>Interest FY 2025-26</b>.</p>
  ),
});

const providentHoldings = (sortable) => (
  <>
    <p>One row per account. <b>Value</b> is the total of the deposits you've entered and <b>Allocation</b> is its share of your total.</p>
    <p><b>Click a row</b> to see every deposit, then use the <b>⋮</b> button to edit or delete one.{sortable ? ' Tap a column heading to sort.' : ''}</p>
  </>
);

// Product pages under Investments → Debt.
export const DEBT_TOURS = {
  'inv-debt-govt-bonds': priceUnitsTour({
    name: 'Government Bonds',
    intro: (
      <>
        <p>Government bonds (G-Secs, T-bills, State Development Loans) are loans you give to the central or a state government. They pay a fixed interest and are among the safest debt investments.</p>
        <p>Sovereign Gold Bonds belong under <b>Gold</b>, not here.</p>
      </>
    ),
    label: 'Bond Series',
    plural: 'bond series',
    tip: 'Type the name the same way each time.',
  }),

  'inv-debt-corp-bonds': priceUnitsTour({
    name: 'Corporate Bonds',
    intro: (
      <p>Corporate bonds are loans you give to a company, such as a bank or an NBFC. They usually pay more interest than government bonds, but carry more risk. A higher credit rating (AAA is the best) means lower risk.</p>
    ),
    label: 'Bond Series',
    plural: 'bonds',
    tip: 'Type the name the same way each time.',
  }),

  'inv-debt-ncd': priceUnitsTour({
    name: 'NCDs',
    intro: (
      <p>Non-Convertible Debentures (NCDs) are loans to a company that pay a fixed interest. Unlike some debentures, they can never be converted into the company's shares. They're often issued at ₹1,000 each.</p>
    ),
    label: 'NCD Series',
    plural: 'NCDs',
    tip: 'Include the series in the name, as each series has its own rate and maturity.',
  }),

  'inv-debt-others': priceUnitsTour({
    name: 'Other Debt Investments',
    intro: (
      <p>For any debt investment that doesn't fit the other pages, such as National Savings Certificates, Kisan Vikas Patra or the Senior Citizens' Savings Scheme.</p>
    ),
    label: 'Investment Description',
    plural: 'investments',
    tip: 'For deposits without units, enter the amount as the Price and 1 as the Units.',
  }),

  'inv-debt-nps': productTour({
    name: 'NPS Debt Component',
    intro: (
      <>
        <p>Your National Pension System money is split between schemes. Record the debt part here: <b>Scheme C</b> (corporate bonds) and <b>Scheme G</b> (government bonds).</p>
        <p>The equity part (Scheme E) goes under <b>Equity → NPS</b>.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Scheme Name</b>, the <b>Date of Purchase</b>, the <b>NAV</b> (price per unit) on that day and the <b>Amount Invested</b>. <b>Number of Units</b> is worked out for you (Amount ÷ NAV).</p>
        <p>You'll find the NAV and units on your NPS statement. Pick an existing scheme from the list to group contributions together.</p>
      </>
    ),
    summary: (
      <p><b>Total</b> is the amount you've contributed to the debt schemes. Live NAVs aren't fetched here, so it shows what you paid, not today's value.</p>
    ),
    holdings: (
      <>
        <p>One row per scheme, combining all your contributions to it. <b>Value</b> is what you paid and <b>Allocation</b> is its share of your total.</p>
        <p><b>Click a row</b> to see every contribution, then use the <b>⋮</b> button to edit or delete one. Tap a column heading to sort.</p>
      </>
    ),
    ...BACK,
  }),

  'inv-debt-fd': productTour({
    name: 'Fixed Deposits',
    intro: (
      <p>A fixed deposit (FD) locks your money with a bank or company for a set period at a fixed interest rate. Add each FD here and Mickles works out what you'll get back at maturity.</p>
    ),
    add: (
      <>
        <p>Give each FD its own <b>Deposit Name</b> (for example "SBI FD 2025"). Names can't repeat.</p>
        <p>Enter the <b>Date of Deposit</b>, <b>Amount Invested</b>, <b>Interest Rate (%)</b>, both frequencies, and the tenure in <b>Years</b>, <b>Months</b> and <b>Days</b>. Or leave the tenure empty and pick the <b>Maturity Date</b> instead.</p>
      </>
    ),
    summary: (
      <p><b>Total</b> is the money you've put into FDs (the principal), not including interest.</p>
    ),
    holdings: (
      <>
        <p>One row per FD, with its rate, <b>Tenure</b> and <b>Maturity Date</b>.</p>
        <p><b>Click a row</b> to see the interest it earns, and use the <b>⋮</b> button there to edit or delete it. Tap a column heading to sort.</p>
      </>
    ),
    charts: <p>How your FD money is split between deposits, by amount invested.</p>,
    extraSteps: [
      {
        target: '[data-tour="inv-fd-maturity"]',
        title: 'Maturity value',
        body: (
          <>
            <p>Worked out from the amount, rate, tenure and <b>Compounding Frequency</b> (how often interest is added; quarterly for most banks).</p>
            <p>Choose <b>At Maturity</b> as the payout for a cumulative FD. If interest is paid out to you monthly or quarterly, the maturity value is just your principal; click the row to see the interest per payout.</p>
          </>
        ),
      },
    ],
    ...BACK,
  }),

  'inv-debt-recurring-deposit': productTour({
    name: 'Recurring Deposits',
    intro: (
      <p>In a recurring deposit (RD) you pay a fixed amount every month, and get it all back with interest at the end. Add each RD once and Mickles keeps track of the instalments for you.</p>
    ),
    add: (
      <>
        <p>Enter a unique <b>Account Name</b>, the <b>Start Date</b>, your <b>Monthly Investment</b>, the <b>Interest Rate (%)</b> and the tenure in years and months.</p>
        <p>You don't need to add each month's payment. Instalments are counted from the start date.</p>
      </>
    ),
    summary: (
      <p><b>Current Value</b> is what you've paid in so far: your monthly amount × the instalments due since each start date. It doesn't include interest.</p>
    ),
    holdings: (
      <>
        <p>One row per RD, with its <b>Instalment</b>, rate and <b>Maturity Date</b>. <b>Contribution</b> is what you've paid in so far.</p>
        <p><b>Click a row</b> to see its details, then use the <b>⋮</b> button to edit or delete it. Tap a column heading to sort.</p>
      </>
    ),
    charts: <p>How your RDs compare by maturity value.</p>,
    extraSteps: [
      {
        target: '[data-tour="inv-rd-instalments"]',
        title: 'Instalments paid',
        body: (
          <p>The <b>Tenure (Months)</b> column shows instalments paid out of the total (for example <b>8/12</b>), with a progress bar. One is counted each month on the start date's day, so a missed payment isn't shown.</p>
        ),
      },
      {
        target: '[data-tour="inv-rd-maturity"]',
        title: 'Maturity value',
        body: (
          <p>What you'll get back at the end: all your instalments plus interest, compounded every quarter as Indian banks do.</p>
        ),
      },
    ],
    ...BACK,
  }),

  'inv-debt-mutual-fund': productTour({
    name: 'Debt Mutual Funds',
    intro: (
      <p>Debt mutual funds invest in bonds, government securities and money-market instruments. Liquid, gilt and corporate bond funds are all debt funds. Add each purchase and Mickles tracks what you paid and what it's worth today.</p>
    ),
    add: (
      <>
        <p>Enter the <b>Name of Fund</b>, its <b>Symbol Code</b>, the date, the <b>NAV</b> you bought at and the <b>Amount Invested</b>. <b>Number of Units</b> is worked out for you (Amount ÷ NAV).</p>
        <p>The symbol is the fund's code on Yahoo Finance (it starts with <b>0P</b>) and is needed for live NAVs. Pick an existing fund from the list and its symbol is filled in.</p>
      </>
    ),
    live: (
      <p>NAVs aren't loaded automatically. Tap <b>Get Live NAV</b> to fetch the latest NAV for each fund and see its current value and profit or loss. A fund whose symbol isn't found keeps showing what you paid.</p>
    ),
    summary: (
      <p><b>Invested</b> is what you paid in total. After you tap <b>Get Live NAV</b>, <b>Current Value</b> shows what your funds are worth today, with a <b>Live</b> badge.</p>
    ),
    holdings: (
      <>
        <p>One row per fund, combining all your purchases, with its <b>Invested Value</b>, <b>Current Value</b> and profit or loss (<b>P/L</b>).</p>
        <p><b>Click a row</b> to see every purchase, then use the <b>⋮</b> button to edit or delete one. Tap a column heading to sort.</p>
      </>
    ),
    charts: (
      <p><b>Current Distribution</b> splits your money by today's value (or what you paid, until NAVs are fetched). <b>Invested Distribution</b> uses what you paid.</p>
    ),
    extraSteps: [
      {
        title: 'Fixing a symbol',
        body: (
          <p>No live NAV for a fund? Click its row and tap <b>Edit Symbol</b> at the top. The new symbol is saved for every purchase of that fund.</p>
        ),
      },
    ],
    ...BACK,
  }),

  'inv-debt-ppf': productTour({
    name: 'Public Provident Fund',
    intro: (
      <p>The Public Provident Fund (PPF) is a 15-year government savings scheme with tax-free interest. You can deposit ₹500 to ₹1.5 lakh in each financial year.</p>
    ),
    add: (
      <p>Pick or type the <b>Account Name</b>, then enter the <b>Date of Deposit</b> and the <b>Amount Invested</b>. Add each deposit separately under the same account name.</p>
    ),
    summary: <p><b>Total</b> is the sum of all the deposits you've entered.</p>,
    holdings: providentHoldings(false),
    extraSteps: [providentInterestStep('PPF')],
    ...BACK,
  }),

  'inv-debt-epf': productTour({
    name: 'Employee Provident Fund',
    intro: (
      <p>The Employees' Provident Fund (EPF) is the retirement savings your employer runs for you. Each month part of your salary, plus your employer's share, goes into it.</p>
    ),
    add: (
      <>
        <p>Pick or type the <b>Account Name</b> (for example your employer), then enter the <b>Date of Deposit</b> and the <b>Amount Invested</b>.</p>
        <p>Add contributions from your EPF passbook, or your current balance as a single entry to get started.</p>
      </>
    ),
    summary: <p><b>Total</b> is the sum of all the deposits you've entered.</p>,
    holdings: providentHoldings(true),
    extraSteps: [providentInterestStep('EPF')],
    ...BACK,
  }),
};
